/**
 * CreateSlideoutContext — the contract every CRM create surface talks to.
 *
 * Opening a create form is a function call: `useCreateSlideout().open(config)`.
 * The provider that owns the stack and renders the forms lives in
 * `./CreateSlideoutProvider` — kept separate so a component rendered INSIDE
 * a create form (the association picker's nested "Create new") can import
 * these hooks without pulling the form back in through an import cycle.
 *
 * Dismissal is chrome-owned. Escape, outside click, the header close, the
 * back chevron and the footer Cancel all route through `requestClose()`,
 * which consults the top entry's unsaved-changes registration before popping.
 * Content reports edits with `useCreateDismissGuard(hasUserEdits)`; content
 * that never calls it closes immediately. `pop(result)` is the programmatic
 * close a form calls after a successful submit — it deliberately bypasses the
 * guard because there is nothing left to lose.
 *
 * @example
 * ```tsx
 * const { open } = useCreateSlideout();
 * open(buildGenericCreateConfig({ orgId, entityType: 'ahoy_contact', ... }));
 * ```
 */

import { createContext, useContext, useEffect } from 'react';

import type { CrmCreateConfig } from './types';

// ---------------------------------------------------------------------------
// Stack API
// ---------------------------------------------------------------------------

export interface OpenCreateSlideoutOptions {
  /** Receives whatever the form's `onSubmit` resolved with, once it pops. */
  onResult?: (result: unknown) => void;
}

export interface CreateSlideoutContextValue {
  /** Push a create form onto the stack. */
  open: (config: CrmCreateConfig, options?: OpenCreateSlideoutOptions) => void;
  /**
   * Pop the top form immediately, no questions asked — the programmatic
   * close after a successful submit. Anything that represents the user
   * abandoning the form must go through `requestClose` instead.
   */
  pop: (result?: unknown) => void;
  /**
   * Ask to dismiss the top form, honouring its unsaved changes. Prompts when
   * the top entry registered edits via `useCreateDismissGuard`; otherwise
   * exactly `pop()`.
   */
  requestClose: () => void;
  /** Whether any create form is open. */
  isOpen: boolean;
  /** Current stack depth. */
  depth: number;
  /** Titles of every open form, bottom to top, for the nested breadcrumb. */
  titles: string[];
}

/** No-op implementation for when no provider is present (tests, isolated usage). */
const NO_OP_CONTEXT: CreateSlideoutContextValue = {
  open: () => {},
  pop: () => {},
  requestClose: () => {},
  isOpen: false,
  depth: 0,
  titles: [],
};

export const CreateSlideoutContext =
  createContext<CreateSlideoutContextValue | null>(null);
CreateSlideoutContext.displayName = 'CreateSlideoutContext';

/**
 * Access the create-slideout stack. Returns a no-op implementation outside a
 * provider so forms render standalone (and in tests) unchanged.
 */
export function useCreateSlideout(): CreateSlideoutContextValue {
  const ctx = useContext(CreateSlideoutContext);
  return ctx ?? NO_OP_CONTEXT;
}

// ---------------------------------------------------------------------------
// Per-entry contexts
// ---------------------------------------------------------------------------

/** Whether the enclosing entry is the visible top of the stack. */
export const CreateEntryIsTopContext = createContext(true);
CreateEntryIsTopContext.displayName = 'CreateEntryIsTopContext';

/**
 * Whether the calling form is the visible top entry. Forms gate keyboard
 * shortcuts on this so a hidden parent never submits behind its child.
 * True outside a provider.
 */
export function useIsTopCreateSlideout(): boolean {
  return useContext(CreateEntryIsTopContext);
}

/**
 * Per-entry seam that lets form content report unsaved input to the chrome
 * that owns dismissal. Carries that one fact upward, and nothing else.
 */
export interface CreateEntryGuardRegistration {
  entryId: number;
  setEntryDirty: (entryId: number, isDirty: boolean) => void;
}

export const CreateEntryGuardContext =
  createContext<CreateEntryGuardRegistration | null>(null);
CreateEntryGuardContext.displayName = 'CreateEntryGuardContext';

/**
 * Report whether this form has unsaved user input. While true, every
 * dismissal the chrome owns routes through a confirmation instead of
 * discarding silently.
 *
 * `hasUnsavedChanges` must mean *the user changed something*, not *the form
 * differs from empty* — a form that reports dirty while populating its own
 * defaults trains people to dismiss the confirmation reflexively.
 *
 * No-ops outside a provider.
 */
export function useCreateDismissGuard(hasUnsavedChanges: boolean): void {
  const registration = useContext(CreateEntryGuardContext);

  // Synchronizing with the chrome's registry is what effects are for; the
  // cleanup covers both unmount (the entry popped) and re-registration.
  useEffect(() => {
    if (!registration) {
      return;
    }
    const { entryId, setEntryDirty } = registration;
    setEntryDirty(entryId, hasUnsavedChanges);
    return () => {
      setEntryDirty(entryId, false);
    };
  }, [registration, hasUnsavedChanges]);
}
